import { useState, useCallback, useRef, useEffect } from "react"
import { useNavigate } from "react-router-dom";

import { toast } from "react-toastify";
import { playError, playSuccess, playComplete } from "../helpers/audioPlayer";
import { CheckCircle, ArrowLeft, X, ChevronUp, ChevronDown } from "lucide-react";
import { logger } from "../helpers/logHandler";
import { logoutProcessor } from "../helpers/logoutProcessor";
import { getLocationIndex, setLocationIndex } from "../helpers/sorterTwoLocationCache";
import { useTranslation } from "../../i18n";
import HiddenScanInput from "../HiddenScanInput";
import ScanStatusIcons from "../ScanStatusIcons";
import ScanFlashOverlay from "../ScanFlashOverlay";
import FitWord from "../FitWord";
import {
    useProgressTimer,
    useStreak,
    useBestTime,
    useSessionScore,
    ProgressBar,
    TimeBonusPopup,
    ScoreDisplay,
} from "../gamification";
import { apiFetch } from "../../api/apiFetch";
import "../../styles/scansheet.css";

type zoneSorter1 = {
    warehouse: string,
    bay: string,
    lane: string,
    additionalLanes: string[],
    batch: number,
    date: string,
    resetFunc: () => void,
    setLane: React.Dispatch<React.SetStateAction<string>>
}

// Fallback per-location timer durations (ms). Warehouse settings (fetched
// from /settings/{warehouse}.sorter_2_location_timers) override these per
// prefix when present. Prefix = first 6 chars of the scanned location.
const LOCATION_TIMERS: Record<string, number> = {
    PDS201: 6000,
    PDS202: 6000,
    NPS201: 6000,
    NPS202: 6000,
    NPS203: 5500,
    NPS204: 5500,
    NPS205: 5500,
    NPS206: 5000,
    NPS207: 6000,
    PLS201: 6000,
    PLS202: 6000,
    PLS203: 6000,
    PLS204: 6000,
    PLS205: 5500,
    PLS206: 5500,
    PLS207: 5000,
    PLS208: 6000,
};
const DEFAULT_TIMER = 6000;

const ZoneStorageSorter2Scanner = ({ warehouse, bay, lane, additionalLanes, batch, date, resetFunc, setLane }: zoneSorter1) => {

    const navigator = useNavigate();
    const { t } = useTranslation();

    const [scan, setScan] = useState<string>("");
    const [lastScanned, set_lastScanned] = useState<string>("");
    const [item, set_item] = useState<string>("");
    const [itemWeight, set_itemWeight] = useState<number>(0);
    const [location, set_Location] = useState<string[]>([]);
    const [fullLocation, set_fullLocation] = useState<string[]>([]);
    const [orderComplete, set_orderComplete] = useState<boolean>(false);
    const [scanStatus, set_scanStatus] = useState<"idle" | "success" | "error" | "complete">("idle");
    const statusTimerRef = useRef<NodeJS.Timeout | null>(null);
    const [errorPopUp, set_errorPopUp] = useState<boolean>(false);
    const [errorRescan, set_errorRescan] = useState<boolean>(false);
    const [orderNumber, set_orderNumber] = useState<string>("");
    const [locationIndex, set_locationIndex] = useState<number>(0);

    // The moving head follows THIS screen. An order can span several slots and
    // the operator pages between them (and the remembered index can restore to
    // something other than the first), so the Backend can't know which one is
    // being read — only this device does. `litLocationRef` is the location the
    // beam is currently on, so we only speak up when the screen moves off it.
    // The scan itself already lights locations[0], which is where the screen
    // opens, so the common single-location case sends nothing extra.
    const litLocationRef = useRef<string | null>(null);
    const orderCompleteRef = useRef<boolean>(false);
    orderCompleteRef.current = orderComplete;

    const aimLightAt = useCallback((fullLocationCode: string | null, { off = false } = {}) => {
        if (!fullLocationCode) return;
        litLocationRef.current = off ? null : fullLocationCode;
        apiFetch(`/zonestorage/scan/aim`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ location: fullLocationCode, off, complete: orderCompleteRef.current }),
        }).catch(() => { /* the beam recentres on its own dwell — never block a scan */ });
    }, []);

    const timer = useProgressTimer();
    const streakApi = useStreak();
    const bestTime = useBestTime("s2-best-time");
    const score = useSessionScore();

    // Warehouse-configured per-prefix overrides for the location timer.
    // Falls back to LOCATION_TIMERS / DEFAULT_TIMER if a prefix isn't set.
    const customLocationTimersRef = useRef<Record<string, number>>({});

    const getTimerDuration = useCallback((fullLocations: string[]): number => {
        if (fullLocations.length === 0) return DEFAULT_TIMER;
        const prefix = fullLocations[0].slice(0, 6).toUpperCase();
        return customLocationTimersRef.current[prefix]
            ?? LOCATION_TIMERS[prefix]
            ?? DEFAULT_TIMER;
    }, []);

    useEffect(() => {
        if (!warehouse) return;
        let alive = true;
        (async () => {
            try {
                const res = await apiFetch(
                    `/settings/${encodeURIComponent(warehouse)}`,
                    {},
                );
                if (!res.ok) return;
                const data = await res.json();
                if (!alive) return;
                if (data.sorter_2_location_timers && typeof data.sorter_2_location_timers === "object") {
                    customLocationTimersRef.current = data.sorter_2_location_timers;
                }
            } catch {
                // leave defaults
            }
        })();
        return () => { alive = false; };
    }, [warehouse]);

    const [timeBonus, set_timeBonus] = useState<number | null>(null);
    const [timeBonusColor, set_timeBonusColor] = useState<"green" | "amber">("green");
    const [isBestTime, set_isBestTime] = useState<boolean>(false);
    const timeBonusRef = useRef<NodeJS.Timeout | null>(null);
    const timeBonusKeyRef = useRef<number>(0);

    const flashScanStatus = (status: "success" | "error" | "complete") => {
        if (statusTimerRef.current) clearTimeout(statusTimerRef.current);
        set_scanStatus(status);
        const duration = status === "complete" ? 1000 : 500;
        statusTimerRef.current = setTimeout(() => set_scanStatus("idle"), duration);
    };

    const reset_preface = async () => {
        await apiFetch(`/zonestorage/lanes/change-in-use-status`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify({ warehouse: warehouse, lane: lane, bay: bay, date: date, batch: batch, inUse: false, user: "" })
        }).then(async (res): Promise<any> => {
            const response = await res.json();
            if (res.ok) {
                // noop
            } else if (res.status === 401) {
                logoutProcessor();
                navigator("/login");
            } else {
                playError();
                console.log(response.message);
                return;
            }
        });

        setLane("");
        resetFunc();
    };

    const closeErrorPopUp = () => {
        set_errorPopUp(false);
    };

    // Score one completed full cycle (item scan → next item scan): award the
    // time bonus + streak if finished within the window, reset the streak if it
    // ran over. No-op when no cycle was running (e.g. the very first item).
    const awardCycle = useCallback(() => {
        const { elapsedMs, completed, durationMs } = timer.stop();
        if (durationMs <= 0) return;
        if (!completed) {
            const secondsLeft = Math.ceil((durationMs - elapsedMs) / 1000);
            set_timeBonusColor(elapsedMs / durationMs < 0.5 ? "green" : "amber");

            const { isNewBest, prevExisted } = bestTime.check(elapsedMs);
            set_isBestTime(isNewBest && prevExisted);

            streakApi.increment();
            score.addSeconds(secondsLeft, "sorter_2");

            timeBonusKeyRef.current += 1;
            set_timeBonus(secondsLeft);
            if (timeBonusRef.current) clearTimeout(timeBonusRef.current);
            timeBonusRef.current = setTimeout(() => {
                set_timeBonus(null);
                set_isBestTime(false);
            }, 1500);
        } else {
            set_timeBonus(null);
            set_isBestTime(false);
            streakApi.reset();
        }
    }, [timer, bestTime, streakApi, score]);

    const doSubmit = useCallback(async (scannedValue?: string) => {
        const val = scannedValue ?? scan;
        const scannedItem = val;
        setScan("");

        if (scannedItem.length < 5 || scannedItem.length > 20) {
            set_orderComplete(false);
            flashScanStatus("error");
            playError();
            toast.dismiss(); toast.error(t("invalid_scan"));
            return;
        }

        if (errorRescan) {
            if (val === item) {
                flashScanStatus(orderComplete ? "complete" : "success");
                orderComplete ? playComplete() : playSuccess();
                set_errorRescan(false);
            } else {
                flashScanStatus("error");
                playError();
            }

            return;
        }

        // Showing a slot code ("A1", "A15") → this scan is the location confirm.
        if (location.length > 0 && /^[A-Z]\d{1,2}$/.test(location[0])) {

            if (fullLocation.some((loc) => loc === scannedItem)) {
                // Placement confirmed. The cycle timer is NOT stopped here — it
                // keeps running until the next item is scanned (the full cycle),
                // which is when the bonus is awarded.
                set_item("");
                set_Location([]);
                set_fullLocation([]);
                // Placement confirmed — the screen is back to SCAN ITEM, so
                // there is nothing for the beam to point at. Mirrors Pick &
                // Sort's confirm-location blackout.
                aimLightAt(scannedItem, { off: true });
                // Location confirmed — jump straight back to SCAN ITEM with no
                // success-flash delay (the green tick is gone, so the 500ms flash
                // would only blank the prompt). Audio still confirms the placement.
                if (statusTimerRef.current) clearTimeout(statusTimerRef.current);
                set_scanStatus("idle");
                playSuccess();
                set_orderComplete(false);
                const logStatus = lastScanned === scannedItem ? "repeat" : "success";
                logger(warehouse, "S2 Loader", item, itemWeight, scannedItem, "Bay Location", "Hand", scannedItem, logStatus);
            } else {
                flashScanStatus("error");
                playError();
                const logStatus = lastScanned === scannedItem ? "repeat" : "wrong location";
                logger(warehouse, "S2 Loader", item, itemWeight, scannedItem, "Bay Location", "Hand", scannedItem, logStatus);
                set_errorPopUp(true);
                set_errorRescan(true);
                timer.reset();
            }

            set_lastScanned(scannedItem);
            return;
        }

        await apiFetch(`/zonestorage/scan`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify({ warehouse: warehouse, sorter: 2, bay: bay, lane: lane, batch: batch, date: date, itemSku: scannedItem, user: "" })
        }).then(async (res): Promise<any> => {
            let response = await res.json();

            const warehousePrefix = warehouse === "Newport" ? "NP" : warehouse === "Poland" ? "PL" : warehouse === "Pembroke" ? "PD" : "TT";

            if (res.ok) {
                // This item scan ends the previous full cycle — score it.
                awardCycle();
                set_item(scannedItem);
                set_itemWeight(response.weight);
                const locations = response.locations as string[];
                // Slot code is the trailing shelf letter + position ("A1", "A15"),
                // so match it rather than assuming a fixed-width prefix.
                set_Location(locations.map((location: string) => location.match(/[A-Z]\d{1,2}$/)?.[0] ?? location));
                set_fullLocation(locations.map((location: string) => location));
                // Single location: the Backend lit it as part of the scan, so
                // record it and the effect below stays silent. Multiple
                // locations: the Backend deliberately lit NOTHING (it can't
                // know which one this screen opens on — a remembered index can
                // restore to any of them), so leave the ref empty and the
                // effect sends the one aim for whatever ends up shown.
                litLocationRef.current = locations.length === 1 ? (locations[0] ?? null) : null;

                const incomingOrder = String(response.orderNumber ?? "").trim();
                if (incomingOrder) {
                    const cached = getLocationIndex(incomingOrder);
                    if (cached !== null) {
                        set_locationIndex(Math.max(0, Math.min(cached, locations.length - 1)));
                    } else {
                        set_locationIndex(0);
                        setLocationIndex(incomingOrder, 0);
                    }
                } else {
                    set_locationIndex(0);
                }
                set_orderNumber(incomingOrder);

                const scanStatusLog = lastScanned === scannedItem ? "repeat" : "success";
                logger(warehouse, "S2 Loader", scannedItem, itemWeight, scannedItem, "Item", warehousePrefix + "L" + String(lane), "Hand", scanStatusLog);

                const isComplete = response.orderCompleteCheck === true && response.orderComplete === "Order complete";
                if (isComplete) {
                    set_orderComplete(true);
                    flashScanStatus("complete");
                    playComplete();
                } else {
                    flashScanStatus("success");
                    playSuccess();
                }

                timer.start(getTimerDuration(locations));

            } else if (res.status === 401) {
                logoutProcessor();
                navigator("/login");
            } else {
                set_Location([]);
                set_item("");
                const scanStatusLog = lastScanned === scannedItem ? "repeat" : "error";
                flashScanStatus("error");
                playError();
                toast.dismiss(); toast.error(t(response.error || "Scan failed"));
                logger(warehouse, "S2 Loader", scannedItem, 0, scannedItem, "Item", warehousePrefix + "L" + String(lane), "Hand", scanStatusLog);
            }
        })

        set_lastScanned(scannedItem);
    }, [scan, item, itemWeight, location, fullLocation, errorRescan, lastScanned, warehouse, bay, lane, batch, date, navigator, t, orderComplete, timer, awardCycle, aimLightAt]);

    const changeLocationIndex = (direction: "prev" | "next") => {
        const newIndex = direction === "next" ? locationIndex + 1 : locationIndex - 1;
        if (newIndex < 0 || newIndex >= location.length) return;
        set_locationIndex(newIndex);
        if (orderNumber) {
            setLocationIndex(orderNumber, newIndex);
        }
    };

    // Keep the beam on whatever the screen is showing. Covers both ways the
    // displayed slot can move off the one the scan lit: the operator paging
    // through an order's locations, and a remembered index restoring to a
    // location other than the first. Debounced: rapid paging through an
    // order's locations must aim ONCE at where the operator stops — a cue per
    // intermediate click sent the head on a tour of every slot on the way.
    useEffect(() => {
        const shown = fullLocation[locationIndex];
        if (!shown || shown === litLocationRef.current) return;
        const id = setTimeout(() => aimLightAt(shown), 200);
        return () => clearTimeout(id);
    }, [fullLocation, locationIndex, aimLightAt]);

    return (
        <div className="scan-panel">
            <ScanFlashOverlay active={scanStatus === "error"} />
            <div className="page-title-bar">
                <h1 className="page-title">{t("lane_label", { lane: [lane, ...(additionalLanes ?? [])].join(" & ") })}</h1>
                {/* Group the score pill with the back button (matches Pick & Sort) so it
                    doesn't crowd / overlap the title — consistent gap on the right. */}
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <ScoreDisplay total={score.total} />
                    <button className="page-back-btn" onClick={reset_preface}><ArrowLeft size={20} /></button>
                </div>
            </div>

            <TimeBonusPopup
                seconds={timeBonus}
                color={timeBonusColor}
                streak={streakApi.streak}
                isBest={isBestTime}
                bumpKey={timeBonusKeyRef.current}
            />

            <ProgressBar
                progress={timer.state.progress}
                visible={timer.state.active && !errorRescan}
            />

            <div className="scan-centre">
                {/* Suppress the green success tick — keep the red X (error) and the amber complete tick. */}
                {(location.length <= 0 || errorRescan) && <ScanStatusIcons status={scanStatus === "success" || scanStatus === "error" ? "idle" : scanStatus} />}

                {(location.length <= 0 && errorRescan === false) && (
                    <div className={`scan-command ${scanStatus !== "idle" ? "scan-command-hidden" : ""}`}>
                        {t("scan_item").toUpperCase().split(" ").map((word, i) => (
                            <FitWord key={i}>{word}</FitWord>
                        ))}
                    </div>
                )}
                {(location.length > 0 && errorRescan === false) && (
                    <>
                        <div className={`scan-feedback-area s2-location-paginator ${scanStatus === "error" ? "scan-command-hidden" : ""}`}>
                            {/* Green success tick removed; the amber "complete" tick (lane/order done) stays. */}
                            {scanStatus === "complete" && (
                                <div className="s2-location-tick s2-inline-complete">
                                    <CheckCircle size={140} strokeWidth={3} />
                                </div>
                            )}
                            {location.length > 1 && (
                                <button className={`s2-location-nav-btn ${locationIndex <= 0 ? "s2-location-nav-hidden" : ""} ${scanStatus !== "idle" ? "s2-location-nav-hidden" : ""}`} onMouseDown={(e) => e.preventDefault()} onClick={() => changeLocationIndex("prev")} aria-label="Previous location">
                                    <ChevronUp size={52} strokeWidth={3} />
                                </button>
                            )}
                            <div className="zone-sorter-2-single-result">
                                <h1 className={"letter-" + location[locationIndex].charAt(0)}>{location[locationIndex].charAt(0)}</h1>
                                <h1 className={"number-" + location[locationIndex].slice(1)}>{location[locationIndex].slice(1)}</h1>
                            </div>
                            {location.length > 1 && (
                                <button className={`s2-location-nav-btn ${locationIndex >= location.length - 1 ? "s2-location-nav-hidden" : ""} ${scanStatus !== "idle" ? "s2-location-nav-hidden" : ""}`} onMouseDown={(e) => e.preventDefault()} onClick={() => changeLocationIndex("next")} aria-label="Next location">
                                    <ChevronDown size={52} strokeWidth={3} />
                                </button>
                            )}
                            {location.length > 1 && (
                                <span className={`s2-location-counter ${scanStatus !== "idle" ? "s2-location-nav-hidden" : ""}`}>{locationIndex + 1} / {location.length}</span>
                            )}
                        </div>
                    </>
                )}

                {errorRescan && (
                    <>
                        <div className={`scan-command ${scanStatus !== "idle" ? "scan-command-hidden" : ""}`}>
                            {t("scan_item").toUpperCase().split(" ").map((word, i) => (
                                <FitWord key={i}>{word}</FitWord>
                            ))}
                        </div>
                        <div className={`scan-feedback-area ${scanStatus !== "idle" ? "scan-command-hidden" : ""}`}>
                            <h3>{item}</h3>
                        </div>
                    </>
                )}

            </div>

            <HiddenScanInput value={scan} onChange={setScan} onSubmit={doSubmit} disabled={errorPopUp} disableCamera />

            {errorPopUp &&
                <div className="scan-error-pop-up" onClick={() => closeErrorPopUp()}>
                    <div className="scan-error-pop-up-container" onClick={(e) => e.stopPropagation()}>
                        <button className="scan-error-close-btn" onClick={() => closeErrorPopUp()}><X size={28} strokeWidth={2.5} /></button>
                        <h1>{t("wrong_location")}</h1>
                        <h2>{item}</h2>
                    </div>
                </div>
            }

        </div>
    )
};

export default ZoneStorageSorter2Scanner;
